/**
 * Balance Adjustment Modal
 *
 * Modal dialog for manually adjusting a user's loyalty balance — either
 * crediting (positive amount → bonus) or debiting (negative amount →
 * redemption). Operator may optionally backdate the transaction so the
 * displayed history reflects the actual visit time rather than the moment
 * of correction.
 */

import React from "react";
import { useCreate } from "@refinedev/core";
import {
  Modal,
  Form,
  InputNumber,
  Input,
  Button,
  Space,
  Typography,
  message,
  Checkbox,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { TransactionCreateRequest } from "../../../api-client";

const { TextArea } = Input;
const { Text } = Typography;

interface AddBonusModalProps {
  userId: number;
  username?: string | null;
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddBonusModal: React.FC<AddBonusModalProps> = ({
  userId,
  username,
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { mutate: createTransaction } = useCreate();
  const isLoading = false; // TODO: Fix useCreate hook typing

  React.useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        amount: undefined,
        comment: undefined,
        notifyUser: false,
        transactionAt: dayjs(),
      });
    }
  }, [visible, form]);

  const amountValue = Form.useWatch<number | null | undefined>("amount", form);
  const isCredit = typeof amountValue === "number" && amountValue > 0;
  const isDebit = typeof amountValue === "number" && amountValue < 0;
  const submitLabel = isDebit ? "Списать" : "Начислить";

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const amount: number = values.amount;
        const comment =
          values.comment?.trim() || "Ручная корректировка баланса";
        const notifyUser: boolean = values.notifyUser ?? false;
        const transactionAt: Dayjs | undefined = values.transactionAt;

        const transactionType: "bonus" | "redemption" =
          amount > 0 ? "bonus" : "redemption";

        const transactionData: TransactionCreateRequest = {
          user_id: userId,
          amount,
          transaction_type: transactionType,
          description: comment,
          notify_user: notifyUser,
          ...(transactionAt
            ? { transaction_at: transactionAt.toISOString() }
            : {}),
        };

        createTransaction(
          {
            resource: "transactions",
            values: transactionData,
          },
          {
            onSuccess: () => {
              const successText =
                amount > 0
                  ? `Начислено +${amount} баллов`
                  : `Списано ${amount} баллов`;
              message.success(successText);
              form.resetFields();
              onClose();
              onSuccess?.();
            },
            onError: (error: unknown) => {
              const err = error as
                | { response?: { data?: { detail?: string } }; message?: string }
                | undefined;
              const errorMessage =
                err?.response?.data?.detail ||
                err?.message ||
                "Произошла ошибка при изменении баланса";
              message.error(errorMessage);
            },
          },
        );
      })
      .catch((errorInfo) => {
        console.error("Validation failed:", errorInfo);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Корректировка баланса"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      width={500}
      data-testid="add-bonus-modal"
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Text strong>Пользователь:</Text>{" "}
          <Text>
            {username ? `@${username}` : "Без username"} (ID: {userId})
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="amount"
            label="Сумма баллов"
            rules={[
              { required: true, message: "Введите сумму баллов" },
              {
                validator: (_, value) => {
                  if (value === undefined || value === null) {
                    return Promise.resolve();
                  }
                  if (!Number.isInteger(value)) {
                    return Promise.reject(
                      new Error("Сумма должна быть целым числом"),
                    );
                  }
                  if (value === 0) {
                    return Promise.reject(
                      new Error("Сумма не может быть равна 0"),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra="Положительное — начисление, отрицательное — списание"
          >
            <InputNumber
              style={{ width: "100%" }}
              precision={0}
              step={1}
              placeholder="Например: 50 или -30"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            name="transactionAt"
            label="Дата транзакции"
            extra="По умолчанию — сейчас. Можно указать прошлую дату (например, день фактического посещения)."
          >
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="DD.MM.YYYY HH:mm"
              style={{ width: "100%" }}
              allowClear={false}
              disabledDate={(current) =>
                !!current && current.isAfter(dayjs().endOf("day"))
              }
            />
          </Form.Item>

          <Form.Item
            name="comment"
            label="Комментарий для пользователя (опционально)"
            rules={[
              {
                max: 200,
                message: "Комментарий не может превышать 200 символов",
              },
            ]}
          >
            <TextArea
              rows={3}
              maxLength={200}
              showCount
              placeholder="Причина корректировки (например, компенсация за ошибку)"
            />
          </Form.Item>

          <Form.Item
            name="notifyUser"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>
              {isDebit
                ? "Уведомить пользователя о списании"
                : "Уведомить пользователя о начислении"}
            </Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCancel} disabled={isLoading}>
                Отмена
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={isLoading || (!isCredit && !isDebit)}
                danger={isDebit}
              >
                {submitLabel}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};

export default AddBonusModal;
